import { Box, Typography } from "@mui/material";
import { Grid } from "@mui/system";
import { useEffect, useRef } from "react";
import Prism from "prismjs";
import "prismjs/components/prism-json";

type Props = {
  items: any[];
};

const globalStyles = `
  .custom-prism-console {
    background: #ffffff !important;
    color: #333;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 11px; 
    line-height: 1.6;
    margin: 0 !important;
  }
  code[class*="language-"],
  pre[class*="language-"],
  .custom-prism-console code[class*="language-"] {
    background: #ffffff !important;
    text-shadow: none !important;
  }
  .custom-prism-console .token.property { color: #e91e8c; }
  .custom-prism-console .token.string   { color: #2aa198; }
  .custom-prism-console .token.number   { color: #d19a66; }
  .custom-prism-console .token.boolean  { color: #56b6c2; }
  .custom-prism-console .token.null     { color: #56b6c2; }
  .custom-prism-console .token.punctuation { color: #24292e; }
  .custom-prism-console .token.operator    { color: #24292e; }
`;

interface CodeBlockProps {
  code: string;
  label: string;
}

const CodeBlock = ({ code, label }: CodeBlockProps) => {
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [code]);

  const lines = code.split("\n");

  return (
    // Outer fieldset-style box — label on top border like screenshot
    <Box
      sx={{
        position: "relative",
      }}
    >
      {/* Label sits on top border like a fieldset legend */}
      <Typography
        variant="body2"
        sx={{
          position: "absolute",
          top: "-6px",
          left: "8px",
          fontSize: "12px",
          color: "#00000099",
          backgroundColor: "#ffffff",
          px: 0.5,
          lineHeight: 1,
          zIndex: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          border: "1px solid #0000001F",
          position: "relative",
          fontSize: "11px",
          height: "260px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* Single scroll area */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            mt: "10px",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#e0e0e0" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0",
              borderRadius: "2px",
            },
          }}
        >
          {/* Line Numbers */}
          <Box
            sx={{
              textAlign: "right",
              pr: 1.5,
              pl: 1,
              pt: "10px",
              pb: "10px",
              color: "#aaaaaa",
              userSelect: "none",
              lineHeight: "1.6",
              minWidth: "32px",
              fontSize: "11px",
              fontFamily: "Consolas, Monaco, monospace",
              flexShrink: 0,
            }}
          >
            {lines.map((_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </Box>

          {/* Highlighted Code */}
          <Box
            component="pre"
            className="custom-prism-console"
            sx={{
              m: 0,
              pt: "10px",
              pb: "10px",
              pl: 1,
              pr: 1,
              flex: 1,
              overflow: "visible",
              backgroundColor: "#ffffff !important",
              lineHeight: "1.6",
              border: "none",
              outline: "none",
            }}
          >
            <code ref={codeRef} className="language-json">
              {code}
            </code>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const UnifierSettingsCodeConsole = ({ items }: Props) => {
  const { filtering_definition, unique_definition } = items?.[0] || {};

  // Guard: no data at all
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={1}>
        No definition data found.
      </Typography>
    );
  }

  const filteringCode =
    filtering_definition && Object.keys(filtering_definition).length > 0
      ? JSON.stringify(filtering_definition, null, 2)
      : "// No filtering definition available";

  const uniqueCode =
    unique_definition && Object.keys(unique_definition).length > 0
      ? JSON.stringify(unique_definition, null, 2)
      : "// No unique definition available";

  return (
    <>
      <style>{globalStyles}</style>
      <Grid container spacing={3}>
        <Grid size={6}>
          <CodeBlock label="Filtering Definition" code={filteringCode} />
        </Grid>
        <Grid size={6}>
          <CodeBlock label="Unique Definition" code={uniqueCode} />
        </Grid>
      </Grid>
    </>
  );
};

export default UnifierSettingsCodeConsole;
